/**
 * Array of CSS properties that require pixel values.
 * @type {string[]}
 */
export const needsPixelArray = [
  'top',
  'left',
  'width',
  'height',
  'fontSize',
  'letterSpacing',
];

/**
 * Checks if a given element ID has children within a group map object.
 *
 * This function determines whether an element, identified by its ID, has associated
 * children in the provided group map object.
 *
 * @param {string} elementId - The ID of the element to check.
 * @param {Object} groupMapObj - An object where keys represent element IDs and values 
 *      represent associated children or other relevant data.
 *
 * @returns {boolean} Returns `true` if the element has children in the group map, 
 *      otherwise `false`.
 *
 * @example
 * const groupMap = {
 *   'element1': [...children],
 *   'element2': [...children]
 * };
 *
 * hasChildren('element1', groupMap);  // returns true
 * hasChildren('element3', groupMap);  // returns false
 */
export function hasChildren(elementId, groupMapObj) {
  return Object.keys(groupMapObj).includes(elementId);
}

/**
 * Computes the bounding size and position of a group of elements.
 *
 * Given an array of elements with individual styles, this function determines
 * the bounding box that would encompass all those elements. The bounding box's
 * top and left positions are determined by the element in the array that has the
 * smallest top and left positions respectively. Similarly, the width and height
 * of the bounding box are determined by the furthest right and bottom positions
 * of the elements in the array.
 *
 * @param {Object[]} elements - An array of elements where each element has a `styles` property
 *      with `top`, `left`, `width`, and `height` values.
 *      E.g. { styles: { top: 10, left: 20, width: 30, height: 40 } }.
 *
 * @returns {Object|null} An object with properties `top`, `left`, `width`, and `height`
 *      that describe the bounding box's position and size. Returns `null` if the
 *      elements array is empty.
 *
 * @example
 * const elems = [
 *   { styles: { top: 10, left: 20, width: 30, height: 40 } },
 *   { styles: { top: 15, left: 25, width: 35, height: 45 } }
 * ];
 *
 * computeGroupSizeAndPosition(elems);
 * // returns { top: 10, left: 20, width: 40, height: 50 }
 */
export function computeGroupSizeAndPosition(elements) {
  if (!elements.length) {
    return null;
  }

  // 1. Find new group's position
  const groupTop = Math.min(...elements.map((e) => e.styles.top));
  const groupLeft = Math.min(...elements.map((e) => e.styles.left));

  // 2. Find new group's width and height
  const groupRight = Math.max(...elements.map((e) => e.styles.left + e.styles.width));
  const groupBottom = Math.max(...elements.map((e) => e.styles.top + e.styles.height));

  const groupWidth = groupRight - groupLeft;
  const groupHeight = groupBottom - groupTop;

  return {
    top: groupTop,
    left: groupLeft,
    width: groupWidth,
    height: groupHeight,
  };
}

/**
 * Computes the bounding box (top, left, width, height) of the given elements
 * from their stored styles, ignoring any elements without measurable numeric
 * dimensions (e.g. `auto` sized text). Used to give container elements like
 * data providers a bounds box based on their contents, matching how regular
 * group bounds are computed.
 *
 * @param {string[]} elementIds - IDs of the elements to measure.
 * @param {Function} getElementById - A lookup function that returns an element
 *      by its ID (e.g. the `getElementById` store getter).
 * @returns {Object|null} An object with `top`, `left`, `width`, and `height`,
 *      or `null` if no elements have measurable styles.
 */
export function computeElementBounds(elementIds, getElementById) {
  const childStyles = elementIds
    .map((id) => getElementById(id)?.styles)
    .filter((styles) => (
      styles
      && Number.isFinite(styles.top)
      && Number.isFinite(styles.left)
      && Number.isFinite(styles.width)
      && Number.isFinite(styles.height)
    ))
    .map((styles) => ({ styles }));

  return computeGroupSizeAndPosition(childStyles);
}

/**
 * Computes an element's position relative to its group parent.
 *
 * This function calculates the relative position of an element inside a group by
 * adding the element's `top` and `left` position values to those of the group (if provided).
 * If the group's position values are not given, it defaults to 0.
 *
 * @param {Object} elementStyles - An object with properties `top` and `left` representing
 *      the element's position.
 * @param {Object} [groupStyles] - An optional object with properties `top` and `left`
 *      representing the group's position. Defaults to `{ top: 0, left: 0 }` if not provided.
 *
 * @returns {Object} An object with properties `top` and `left` representing the
 *      calculated relative position of the element.
 *
 * @example
 *
 * const elemStyles = { top: 10, left: 20 };
 * const grpStyles = { top: 5, left: 5 };
 *
 * getPositionRelativeToGroupParent(elemStyles, grpStyles);
 * // returns { top: 15, left: 25 }
 */
export function getPositionRelativeToGroupParent(elementStyles, groupStyles) {
  // Add the top and left values of the element
  const top = elementStyles.top + (groupStyles?.top || 0);
  const left = elementStyles.left + (groupStyles?.left || 0);

  return { top, left };
}

/**
 * Returns the CSS transform that counter-translates `social-data` content back
 * into canvas space after the wrapper is anchored at the content's bounds.
 *
 * The negation is computed numerically (`-left` inside the template literal)
 * rather than string-prefixed (`-${left}`): when a bound is negative (e.g. a
 * post card whose left edge sits off-canvas at `left: -3`), the string form
 * produces invalid CSS like `translate3d(--3px, ...)` which browsers silently
 * drop — leaving children positioned relative to the social-data wrapper
 * instead of the canvas.
 *
 * @param {Object} [bounds] - The computed bounds `{ top, left }` of the
 *      social-data content.
 * @returns {Object} A style object with the counter-translation `transform`.
 */
export function getSocialDataContentOffset(bounds) {
  const { top = 0, left = 0 } = bounds || {};

  return {
    transform: `translate3d(${-left}px, ${-top}px, 0px)`,
  };
}
